import { Box, Text } from '@joinrepublic/dls-ui-kit';
import { observer } from 'mobx-react-lite';

import { useGrowthStageOfferingsQuery } from '@r-client/republic/data/offerings';
import { QueryError } from '@r-client/republic/feature/errors';
import { OfferingDealCard } from '@r-client/republic/feature/offering-deal-card';
import {
  DealRailSection,
  DealRailSkeleton,
  SeeAllButton,
} from '@r-client/republic/ui/offerings';
import { ASSET_TYPES } from '@r-client/shared/data/analytics';
import { isInitialQueryLoading } from '@r-client/shared/util/core';

import { useHomepageOpportunitiesAnalytics } from '../../../hooks/use-homepage-opportunities-analytics';
import {
  RAIL_HEADING_LEVEL,
  SECTION_RAIL_BLEED,
  SECTION_RAIL_GUTTER,
  SEE_ALL_HREF,
} from '../constants';
import {
  EMPTY_MESSAGE,
  GROWTH_STAGE_RAIL_QUERY_VARIABLES,
  GROWTH_STAGE_RAIL_SLIDE_WIDTH,
  HEADING,
  LOADING_LABEL,
  MOBILE_CARD_COUNT,
  SUBHEADER,
} from './constants';

/**
 * The live deals, as a rail. `DealRailSection` is the same component funded
 * ventures uses on /investment-opportunities, which is what holds the two to one
 * card size and one set of gutters.
 *
 * No `count`: the design's header is the title and the "See all" link only, and
 * the totals a homepage visitor cares about are in the stats block below.
 *
 * `observer` is required — `BaseQueryModel` only installs its variables autorun
 * once it is MobX-observed, so without this wrapper the rail would sit on the
 * skeleton forever.
 */
export const GrowthStageVenturesRail = observer(
  function GrowthStageVenturesRail() {
    const offeringsQuery = useGrowthStageOfferingsQuery({
      variables: GROWTH_STAGE_RAIL_QUERY_VARIABLES,
      notifyOnNetworkStatusChange: true,
    });
    const { seeAllClicked, assetCardClicked, assetFollowed, assetUnfollowed } =
      useHomepageOpportunitiesAnalytics();

    const offerings = offeringsQuery.data?.offeringsSearch.nodes ?? [];

    // Every "See all" here leads to the same place — the header link and the
    // rail's terminal tile from `sm` up, the full-width button at every width —
    // so all of them report the same event. See the note in
    // `../investment-opportunities.tsx` on why they are one number.
    const onSeeAllClick = () => seeAllClicked(ASSET_TYPES.OFFERING);

    const renderFallback = () => {
      if (isInitialQueryLoading(offeringsQuery))
        return (
          <DealRailSkeleton
            ariaLabel={LOADING_LABEL}
            slideWidth={GROWTH_STAGE_RAIL_SLIDE_WIDTH}
          />
        );

      if (offeringsQuery.isRejected)
        return <QueryError queries={[offeringsQuery]} showRefetch />;

      if (!offerings.length)
        return (
          <Text size="body-md" variant="text-secondary">
            {EMPTY_MESSAGE}
          </Text>
        );

      return undefined;
    };

    const fallback = renderFallback();

    return (
      <>
        <DealRailSection
          title={HEADING}
          subtitle={SUBHEADER}
          headingAs={RAIL_HEADING_LEVEL}
          slideWidth={GROWTH_STAGE_RAIL_SLIDE_WIDTH}
          bleed={SECTION_RAIL_BLEED}
          gutter={SECTION_RAIL_GUTTER}
          mobileLayout="stack"
          mobileCount={MOBILE_CARD_COUNT}
          seeAllHref={SEE_ALL_HREF}
          onSeeAllClick={onSeeAllClick}
          fallback={fallback}
        >
          {offerings.map((offering) => (
            <OfferingDealCard
              key={offering.id}
              offering={offering}
              onCardClick={() =>
                assetCardClicked(ASSET_TYPES.OFFERING, offering.slug)
              }
              onFollow={() =>
                assetFollowed(ASSET_TYPES.OFFERING, offering.slug)
              }
              onUnfollow={() =>
                assetUnfollowed(ASSET_TYPES.OFFERING, offering.slug)
              }
            />
          ))}
        </DealRailSection>

        {/* At every width, unlike the real-world assets one: the design keeps it
            on desktop because the rail's own two affordances are easy to scroll
            past. Suppressed while the section is showing a skeleton, an error or
            empty copy — "See all" under "Something went wrong" is noise. */}
        {!fallback && (
          // On the rail's bleed and gutter rather than the section
          // `Container`'s padding, so the button is exactly as wide as the
          // stacked cards above it — see the note at the same `Box` in
          // ../real-world-assets/real-world-assets.tsx.
          <Box mx={SECTION_RAIL_BLEED} px={SECTION_RAIL_GUTTER} mt="4">
            <SeeAllButton href={SEE_ALL_HREF} onClick={onSeeAllClick} />
          </Box>
        )}
      </>
    );
  }
);
